import { loadBaseCSS } from '../shared/button-utils.js';
import '../sidebar-menu/qds-sidebar-menu.js';
import {
  clearActiveContentDragState,
  setContentDragPayload
} from '../content-blocks/qds-content-drag.js';
import { resolveRegistry } from '../content-blocks/qds-content-blocks.js';
import type { QdsContentMode } from '../../types/content.js';
import type { QdsBlockRegistry } from '../../types/blocks.js';
import type { HierarchicalMenuItem } from '../../types/index.js';

loadBaseCSS();

const DRAG_PREVIEW_FADE_DURATION_MS = 140;
const PALETTE_LINK_PREFIX = '#palette-block=';

interface QdsAddBlockDetail {
  type: string;
}

interface QdsPaletteItemMeta {
  type: string;
  label: string;
  description: string;
}

/**
 * @element qds-content-palette
 *
 * A categorized palette of available block definitions for the current mode, rendered via a sidebar menu, supporting click-to-add and drag-to-canvas creation of new blocks.
 *
 * @attr {string} mode - Content mode used to filter available blocks: `page` or `dashboard`
 * @attr {boolean} readonly - When present, disables adding and dragging blocks from the palette
 *
 * @property {QdsContentMode} mode - Content mode used to filter available blocks
 * @property {QdsBlockRegistry | null} registry - The block registry providing available block definitions
 * @property {boolean} readonly - Whether adding and dragging blocks is disabled
 *
 * @fires add-block - Fired when a palette item is activated, carrying the block type to add
 */
export class QdsContentPalette extends HTMLElement {
  private _mode: QdsContentMode = 'page';
  private _registry: QdsBlockRegistry | null = null;
  private _readonly = false;
  private _dragPreview: HTMLDivElement | null = null;
  private _transparentDragImage: HTMLCanvasElement | null = null;
  private _paletteItemMeta = new Map<string, QdsPaletteItemMeta>();
  private _activeDragItem: HTMLElement | null = null;
  private _boundMenuRoot: ShadowRoot | null = null;

  static get observedAttributes(): string[] {
    return ['mode', 'readonly'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    if (!this.isConnected || !this.shadowRoot) {
      return;
    }

    this.render();
  }

  disconnectedCallback(): void {
    this._stopDragPreviewTracking();
    this._removeDragPreview();
    this._clearActiveDragItem();
    this._detachSidebarMenuListeners();
  }

  private _detachSidebarMenuListeners(): void {
    if (!this._boundMenuRoot) {
      return;
    }
    this._boundMenuRoot.removeEventListener('click', this._handleClick);
    this._boundMenuRoot.removeEventListener('dragstart', this._handleDragStart as EventListener);
    this._boundMenuRoot.removeEventListener('dragend', this._handleDragEnd as EventListener);
    this._boundMenuRoot = null;
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) {
      return;
    }

    if (name === 'mode') {
      this._mode = newValue === 'dashboard' ? 'dashboard' : 'page';
    }

    if (name === 'readonly') {
      this._readonly = newValue !== null;
    }

    this.render();
  }

  get mode(): QdsContentMode {
    return this._mode;
  }

  set mode(value: QdsContentMode) {
    this._mode = value === 'dashboard' ? 'dashboard' : 'page';
    this.setAttribute('mode', this._mode);
  }

  get registry(): QdsBlockRegistry | null {
    return this._registry;
  }

  set registry(value: QdsBlockRegistry | null) {
    this._registry = value;
    this.render();
  }

  get readonly(): boolean {
    return this._readonly;
  }

  set readonly(value: boolean) {
    this._readonly = Boolean(value);
    if (this._readonly) {
      this.setAttribute('readonly', '');
    } else {
      this.removeAttribute('readonly');
    }
  }

  private _handleClick = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    const button = target?.closest<HTMLElement>('[data-action="add-block"]');

    if (!button || this._readonly) {
      if (button) {
        event.preventDefault();
      }
      return;
    }

    event.preventDefault();
    const type = button.dataset.blockType;
    if (!type) {
      return;
    }

    this.dispatchEvent(new CustomEvent<QdsAddBlockDetail>('add-block', {
      bubbles: true,
      composed: true,
      detail: { type }
    }));
  };

  private _handleDragStart = (event: DragEvent): void => {
    if (this._readonly) {
      event.preventDefault();
      return;
    }

    const target = event.target as HTMLElement | null;
    const item = target?.closest<HTMLElement>('[data-block-type]');
    const type = item?.dataset.blockType;

    if (!item || !type || !setContentDragPayload(event, {
      kind: 'palette-block',
      type,
      mode: this._mode
    })) {
      event.preventDefault();
      return;
    }

    this._applyTransparentDragImage(event);
    this._createDragPreview(item);
    this._positionDragPreview(
      event.clientX || (item.getBoundingClientRect().left + 24),
      event.clientY || (item.getBoundingClientRect().top + 24)
    );
    this._startDragPreviewTracking();
    this._clearActiveDragItem();
    this._activeDragItem = item;
    item.classList.add('palette__item--dragging');
    item.style.opacity = '0.55';
    item.style.boxShadow = 'var(--shadow-focus, 0 1px 6px rgba(32, 33, 36, 0.2))';
  };

  private _handleDragEnd = (): void => {
    const hasPreview = Boolean(this._dragPreview);
    this._stopDragPreviewTracking();
    this._fadeOutDragPreview();
    const clearDraggingState = () => this._clearActiveDragItem();
    if (hasPreview) {
      window.setTimeout(clearDraggingState, DRAG_PREVIEW_FADE_DURATION_MS);
    } else {
      clearDraggingState();
    }
    window.setTimeout(() => clearActiveContentDragState(), 0);
  };

  private _applyTransparentDragImage(event: DragEvent): void {
    if (!event.dataTransfer || typeof document === 'undefined') {
      return;
    }

    if (!this._transparentDragImage) {
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      this._transparentDragImage = canvas;
    }

    event.dataTransfer.setDragImage(this._transparentDragImage, 0, 0);
  }

  private _createDragPreview(item: HTMLElement): void {
    if (typeof document === 'undefined') {
      return;
    }

    this._removeDragPreview();

    const label = item.dataset.blockLabel?.trim()
      || item.querySelector('.item-text')?.textContent?.trim()
      || item.textContent?.trim()
      || 'Block';
    const description = item.dataset.blockDescription?.trim()
      || item.getAttribute('title')?.trim()
      || '';

    const preview = document.createElement('div');
    preview.className = 'palette__drag-preview';
    preview.setAttribute('aria-hidden', 'true');
    preview.style.position = 'fixed';
    preview.style.left = '0';
    preview.style.top = '0';
    preview.style.zIndex = '9999';
    preview.style.width = 'min(320px, calc(100vw - 32px))';
    preview.style.display = 'grid';
    preview.style.gap = '8px';
    preview.style.padding = '16px';
    preview.style.border = '1px solid rgba(66, 133, 244, 0.22)';
    preview.style.borderRadius = '8px';
    preview.style.background = 'rgba(255, 255, 255, 0.96)';
    preview.style.boxShadow = '0 12px 24px rgba(32, 33, 36, 0.18)';
    preview.style.color = 'var(--theme-text-primary, #333333)';
    preview.style.fontFamily = "var(--font-family-primary, 'IBM Plex Sans', sans-serif)";
    preview.style.pointerEvents = 'none';
    preview.style.opacity = '1';
    preview.style.transition = 'opacity 120ms ease';
    preview.style.backdropFilter = 'blur(10px)';

    const title = document.createElement('strong');
    title.style.fontSize = '14px';
    title.style.fontWeight = '600';
    title.textContent = label;
    preview.appendChild(title);

    if (description) {
      const body = document.createElement('span');
      body.style.fontSize = '13px';
      body.style.lineHeight = '1.5';
      body.style.color = 'var(--theme-text-secondary, #5f6368)';
      body.textContent = description;
      preview.appendChild(body);
    }

    document.body.appendChild(preview);
    this._dragPreview = preview;
  }

  private _removeDragPreview(): void {
    this._dragPreview?.remove();
    this._dragPreview = null;
  }

  private _fadeOutDragPreview(): void {
    const preview = this._dragPreview;
    if (!preview) {
      return;
    }

    this._dragPreview = null;
    preview.style.opacity = '0';
    window.setTimeout(() => preview.remove(), DRAG_PREVIEW_FADE_DURATION_MS);
  }

  private _positionDragPreview(clientX: number, clientY: number): void {
    if (!this._dragPreview) {
      return;
    }

    this._dragPreview.style.transform = `translate3d(${clientX + 18}px, ${clientY + 18}px, 0)`;
  }

  private _startDragPreviewTracking(): void {
    window.addEventListener('dragover', this._handleWindowDragOver, true);
    window.addEventListener('drop', this._handleWindowDrop, true);
  }

  private _stopDragPreviewTracking(): void {
    window.removeEventListener('dragover', this._handleWindowDragOver, true);
    window.removeEventListener('drop', this._handleWindowDrop, true);
  }

  private _handleWindowDragOver = (event: DragEvent): void => {
    if (!this._dragPreview) {
      return;
    }

    this._positionDragPreview(event.clientX, event.clientY);
  };

  private _handleWindowDrop = (): void => {
    this._stopDragPreviewTracking();
    this._fadeOutDragPreview();
  };

  private _clearActiveDragItem(): void {
    if (!this._activeDragItem) {
      return;
    }

    this._activeDragItem.classList.remove('palette__item--dragging');
    this._activeDragItem.style.opacity = '';
    this._activeDragItem.style.boxShadow = '';
    this._activeDragItem = null;
  }

  private _buildPaletteMenuData(): HierarchicalMenuItem[] {
    const registry = resolveRegistry(this._registry);
    const definitions = registry.listForMode(this._mode);
    const groupedDefinitions = definitions.reduce<Map<string, typeof definitions>>((groups, definition) => {
      const key = definition.category || 'General';
      const existing = groups.get(key) || [];
      existing.push(definition);
      groups.set(key, existing);
      return groups;
    }, new Map());

    this._paletteItemMeta.clear();

    return Array.from(groupedDefinitions.entries()).map(([category, items]) => ({
      name: category,
      linkUrl: '#',
      items: items.map((definition) => {
        const linkUrl = `${PALETTE_LINK_PREFIX}${encodeURIComponent(definition.type)}`;
        const description = definition.description || 'Add this block to the current document.';

        this._paletteItemMeta.set(linkUrl, {
          type: definition.type,
          label: definition.label,
          description
        });

        return {
          name: definition.label,
          description,
          linkUrl,
          target: '_self'
        };
      })
    }));
  }

  private _configureSidebarMenuItems(): void {
    const menu = this.shadowRoot?.querySelector('qds-sidebar-menu') as HTMLElement | null;
    const menuRoot = menu?.shadowRoot;
    if (!menu || !menuRoot) {
      return;
    }

    // Detach from any previously-tracked menu root (render() recreates the
    // sidebar menu) before binding the current one, so listeners never leak.
    this._detachSidebarMenuListeners();
    menuRoot.addEventListener('click', this._handleClick);
    menuRoot.addEventListener('dragstart', this._handleDragStart as EventListener);
    menuRoot.addEventListener('dragend', this._handleDragEnd as EventListener);
    this._boundMenuRoot = menuRoot;

    menuRoot.querySelectorAll<HTMLElement>('a.menu-item[href]').forEach((item) => {
      const linkUrl = item.getAttribute('href') || '';
      const meta = this._paletteItemMeta.get(linkUrl);
      if (!meta) {
        return;
      }

      item.dataset.action = 'add-block';
      item.dataset.blockType = meta.type;
      item.dataset.blockLabel = meta.label;
      item.dataset.blockDescription = meta.description;
      item.setAttribute('title', meta.description);
      item.setAttribute('target', '_self');
      item.setAttribute('draggable', this._readonly ? 'false' : 'true');
      item.setAttribute('aria-label', `Add ${meta.label}`);
    });
  }

  render(): void {
    if (!this.shadowRoot) {
      return;
    }

    const registry = resolveRegistry(this._registry);
    const definitions = registry.listForMode(this._mode);
    const menuData = this._buildPaletteMenuData();

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          color: var(--theme-text-primary, #333333);
          font-family: var(--font-family-primary, 'IBM Plex Sans', sans-serif);
        }

        .palette {
          display: grid;
          gap: 12px;
        }

        .palette__header {
          display: flex;
          align-items: center;
          min-height: 32px;
        }

        .palette__title {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
        }

        .palette__menu {
          display: block;
          width: 100%;
          min-width: 0;
          height: auto;
          --sidebar-width: 100%;
          --sidebar-content-top-offset: 0;
          --sidebar-content-padding: 0 0 8px;
          --sidebar-category-spacing: 12px;
          --sidebar-item-border-radius: 8px;
          --sidebar-nested-indent: 0;
          --sidebar-level-0-text-transform: uppercase;
          --sidebar-level-0-font-size: var(--theme-font-size-xs, 12px);
          --sidebar-level-0-font-weight: var(--theme-font-weight-semibold, 600);
          --sidebar-level-0-letter-spacing: 0.12em;
          --sidebar-level-0-padding: 0 0 8px;
          --sidebar-level-1-padding: 8px 12px;
          --sidebar-bg-color: transparent;
          --sidebar-border-color: transparent;
          --sidebar-section-color: var(--theme-text-secondary, #5f6368);
          --sidebar-item-color: var(--theme-text-primary, #333333);
          --sidebar-item-hover-bg-color: var(--theme-background-secondary, #f8f8f8);
          --sidebar-item-active-bg-color: var(--theme-background-secondary, #f8f8f8);
          --sidebar-item-active-color: var(--theme-text-primary, #333333);
          --sidebar-item-active-border-left: 0 solid transparent;
        }

        .palette__empty {
          padding: 18px 16px;
          border: 1px dashed var(--theme-border-color, #e0e0e0);
          border-radius: 8px;
          color: var(--theme-text-secondary, #5f6368);
          background: var(--theme-background-secondary, #f8f8f8);
        }
      </style>
      <section class="palette">
        <header class="palette__header">
          <h2 class="palette__title">Block Palette</h2>
        </header>
        ${definitions.length === 0
          ? '<div class="palette__empty">No block definitions are registered for this mode.</div>'
          : '<qds-sidebar-menu id="palette-menu" class="palette__menu" always-expanded></qds-sidebar-menu>'}
      </section>
    `;

    const menu = this.shadowRoot.querySelector('#palette-menu') as HTMLElement | null;
    if (menu) {
      menu.setAttribute('data', JSON.stringify(menuData));
      this._configureSidebarMenuItems();
    }
  }
}

if (!customElements.get('qds-content-palette')) {
  customElements.define('qds-content-palette', QdsContentPalette);
}

export default QdsContentPalette;
